import React, { useState } from 'react';
import { Settings, Bell, Calendar, Clock, Save, PlayCircle, Target } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент формы настройки таймера обратного отсчёта
 */
const TimerForm = ({ 
  formData, 
  setFormData, 
  timerThemes, 
  soundOptions, 
  errors, 
  setErrors, 
  saveTimer,
  startTimer,
  isRunning,
  russianHolidays
}) => {
  const [activeTab, setActiveTab] = useState('basic'); // 'basic', 'appearance', 'notifications'
  
  // Обработчик изменения полей ввода
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: fieldValue
    }));

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Обработчик выбора режима таймера
  const handleTimerModeChange = (mode) => {
    setFormData(prev => ({
      ...prev,
      timerMode: mode
    }));
  };

  // Обработчик сохранения таймера
  const handleSaveTimer = () => {
    if (!formData.timerName) {
      setErrors(prev => ({ ...prev, timerName: 'Пожалуйста, укажите название таймера' }));
      return;
    }
    saveTimer();
  };

  // Обработчик запуска таймера
  const handleStartTimer = (e) => {
    e.preventDefault();
    startTimer();
  };

  // Выбор праздника из списка
  const handleSelectHoliday = (e) => {
    const selectedId = e.target.value;
    if (selectedId === '') return;
    
    const holiday = russianHolidays.find(h => h.id === selectedId);
    if (holiday) {
      setFormData(prev => ({
        ...prev,
        timerName: `До ${holiday.name}`,
        targetDate: holiday.date,
        timerType: 'countdown',
        timerMode: 'date'
      }));
    }
  };

  // Расчет максимальной даты (2 года вперед от текущей)
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 2);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  // Вспомогательная функция для отображения класса кнопки вкладки
  const getTabButtonClass = (tabName) => {
    return `py-2 px-4 text-sm font-medium rounded-t-lg ${
      activeTab === tabName
        ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
    }`;
  };

  return (
    <form onSubmit={handleStartTimer} className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Settings className="w-6 h-6 text-indigo-500" />
        Настройки таймера
      </h2>

      {/* Вкладки настроек */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-6">
        <button
          type="button"
          onClick={() => setActiveTab('basic')}
          className={getTabButtonClass('basic')}
        >
          Основные настройки
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('appearance')}
          className={getTabButtonClass('appearance')}
        >
          Внешний вид
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('notifications')}
          className={getTabButtonClass('notifications')}
        >
          Уведомления
        </button>
      </div>

      {/* Общая ошибка */}
      {errors.general && (
        <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
          {errors.general}
        </div>
      )}

      {/* Основные настройки */}
      {activeTab === 'basic' && (
        <>
          {/* Режим и тип таймера */}
          <div className="mb-6">
            <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Режим таймера
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => handleTimerModeChange('date')}
                className={`p-3 text-sm rounded-lg border transition-colors ${
                  formData.timerMode === 'date' 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                <div className="font-medium flex items-center justify-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>До указанной даты</span>
                </div>
                <div className="text-xs opacity-75 mt-1">Отсчёт до конкретного события</div>
              </button>
              <button
                type="button"
                onClick={() => handleTimerModeChange('timer')}
                className={`p-3 text-sm rounded-lg border transition-colors ${
                  formData.timerMode === 'timer' 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                <div className="font-medium flex items-center justify-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Таймер</span>
                </div>
                <div className="text-xs opacity-75 mt-1">Указать часы, минуты, секунды</div>
              </button>
            </div>
          </div>

          {formData.timerMode === 'date' ? (
            /* Настройки для режима "До указанной даты" */
            <>
              {/* Название таймера */}
              <div className="mb-4">
                <label htmlFor="timerName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Название таймера
                </label>
                <input
                  type="text"
                  id="timerName"
                  name="timerName"
                  value={formData.timerName}
                  onChange={handleInputChange}
                  className={`form-input w-full ${errors.timerName ? 'input-error' : ''}`}
                  placeholder="Обратный отсчёт до..."
                />
                {errors.timerName && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.timerName}
                  </p>
                )}
              </div>

              {/* Быстрый выбор праздника */}
              <div className="mb-4">
                <label htmlFor="holidaySelect" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Выбрать праздник
                </label>
                <select
                  id="holidaySelect"
                  className="form-select w-full"
                  onChange={handleSelectHoliday}
                  value=""
                >
                  <option value="">-- Выберите из популярных дат --</option>
                  {russianHolidays.map(holiday => (
                    <option key={holiday.id} value={holiday.id}>
                      {holiday.name} ({new Date(holiday.date).toLocaleDateString('ru-RU')})
                    </option>
                  ))}
                </select>
              </div>

              {/* Целевая дата */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="targetDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Целевая дата
                  </label>
                  <input
                    type="date"
                    id="targetDate"
                    name="targetDate"
                    value={formData.targetDate}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    max={maxDateStr}
                    className={`form-input w-full ${errors.targetDate ? 'input-error' : ''}`}
                  />
                  {errors.targetDate && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      {errors.targetDate}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="targetTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Целевое время
                  </label>
                  <input
                    type="time"
                    id="targetTime"
                    name="targetTime"
                    value={formData.targetTime}
                    onChange={handleInputChange}
                    className={`form-input w-full ${errors.targetTime ? 'input-error' : ''}`}
                  />
                  {errors.targetTime && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      {errors.targetTime}
                    </p>
                  )}
                </div>
              </div>

              {/* Тип счетчика */}
              <div className="mb-6">
                <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Тип счетчика
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="inline-flex items-center p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600">
                    <input
                      type="radio"
                      name="timerType"
                      value="countdown"
                      checked={formData.timerType === 'countdown'}
                      onChange={handleInputChange}
                      className="form-radio"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Обратный отсчёт (до даты)
                    </span>
                  </label>
                  <label className="inline-flex items-center p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600">
                    <input
                      type="radio"
                      name="timerType"
                      value="countup"
                      checked={formData.timerType === 'countup'}
                      onChange={handleInputChange}
                      className="form-radio"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Прямой отсчёт (от даты)
                    </span>
                  </label>
                </div>
              </div>
            </>
          ) : (
            /* Настройки для режима "Таймер" */
            <>
              {/* Название таймера */}
              <div className="mb-4">
                <label htmlFor="timerName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Название таймера
                </label>
                <input
                  type="text"
                  id="timerName"
                  name="timerName"
                  value={formData.timerName}
                  onChange={handleInputChange}
                  className={`form-input w-full ${errors.timerName ? 'input-error' : ''}`}
                  placeholder="Мой таймер"
                />
                {errors.timerName && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.timerName}
                  </p>
                )}
              </div>

              {/* Время таймера */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Время таймера
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="timerHours" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Часы
                    </label>
                    <input
                      type="number"
                      id="timerHours"
                      name="timerHours"
                      value={formData.timerHours}
                      onChange={handleInputChange}
                      min="0"
                      max="99"
                      className="form-input w-full"
                    />
                  </div>
                  <div>
                    <label htmlFor="timerMinutes" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Минуты
                    </label>
                    <input
                      type="number"
                      id="timerMinutes"
                      name="timerMinutes"
                      value={formData.timerMinutes}
                      onChange={handleInputChange}
                      min="0"
                      max="59"
                      className="form-input w-full"
                    />
                  </div>
                  <div>
                    <label htmlFor="timerSeconds" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Секунды
                    </label>
                    <input
                      type="number"
                      id="timerSeconds"
                      name="timerSeconds"
                      value={formData.timerSeconds}
                      onChange={handleInputChange}
                      min="0"
                      max="59"
                      className="form-input w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Тип счётчика */}
              <div className="mb-6">
                <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Тип счётчика
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="inline-flex items-center p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600">
                    <input
                      type="radio"
                      name="timerType"
                      value="countdown"
                      checked={formData.timerType === 'countdown'}
                      onChange={handleInputChange}
                      className="form-radio"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Обратный отсчёт
                    </span>
                  </label>
                  <label className="inline-flex items-center p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600">
                    <input
                      type="radio"
                      name="timerType"
                      value="countup"
                      checked={formData.timerType === 'countup'}
                      onChange={handleInputChange}
                      className="form-radio"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Прямой отсчёт
                    </span>
                  </label>
                </div>
              </div>
            </>
          )}

          {/* Параметры отображения времени */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Единицы времени для отображения
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="showYears"
                  checked={formData.showYears}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Годы
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="showMonths"
                  checked={formData.showMonths}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Месяцы
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="showDays"
                  checked={formData.showDays}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Дни
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="showHours"
                  checked={formData.showHours}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Часы
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="showMinutes"
                  checked={formData.showMinutes}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Минуты
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="showSeconds"
                  checked={formData.showSeconds}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Секунды
                </span>
              </label>
            </div>
          </div>

          <label className="inline-flex items-center mb-4">
            <input
              type="checkbox"
              name="includeSeconds"
              checked={formData.includeSeconds}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Включить отсчёт секунд
            </span>
          </label>
        </>
      )}

      {/* Настройки внешнего вида */}
      {activeTab === 'appearance' && (
        <>
          {/* Выбор темы */}
          <div className="mb-6">
            <label htmlFor="theme" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Тема оформления
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {timerThemes.map(theme => (
                <div 
                  key={theme.id}
                  onClick={() => handleInputChange({
                    target: { name: 'theme', value: theme.id }
                  })}
                  className={`cursor-pointer p-3 rounded-lg border transition-all ${
                    formData.theme === theme.id 
                      ? 'border-indigo-500 ring-2 ring-indigo-200 dark:ring-indigo-800' 
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div 
                    className="w-full h-6 rounded" 
                    style={{
                      background: theme.id === 'russian-flag' 
                        ? 'linear-gradient(to bottom, white 33%, blue 33%, blue 66%, red 66%)' 
                        : theme.id === 'victory-day'
                        ? 'linear-gradient(to right, #ff7043 50%, #000000 50%)'
                        : `linear-gradient(to right, ${theme.primaryColor}, ${theme.secondaryColor})`
                    }}
                  />
                  <div className="text-sm mt-2 text-center text-gray-700 dark:text-gray-300">{theme.name}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Выбор цветов */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label htmlFor="backgroundColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Цвет фона
              </label>
              <input
                type="color"
                id="backgroundColor"
                name="backgroundColor"
                value={formData.backgroundColor}
                onChange={handleInputChange}
                className="form-input w-full h-10"
              />
            </div>
            <div>
              <label htmlFor="textColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Цвет текста
              </label>
              <input
                type="color"
                id="textColor"
                name="textColor"
                value={formData.textColor}
                onChange={handleInputChange}
                className="form-input w-full h-10"
              />
            </div>
          </div>

          <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <Target className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mr-3 mt-1" />
              <div>
                <h4 className="text-sm font-medium text-indigo-700 dark:text-indigo-300 mb-1">
                  Совет по оформлению
                </h4>
                <p className="text-xs text-indigo-600 dark:text-indigo-400">
                  Для лучшей читаемости выбирайте контрастные цвета фона и текста. 
                  Тема "Российский флаг" идеально подходит для государственных праздников, 
                  а "День Победы" специально разработана для 9 мая.
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Настройки уведомлений */}
      {activeTab === 'notifications' && (
        <>
          {/* Выбор звука */}
          <div className="mb-6">
            <label htmlFor="sound" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Звук уведомления
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {soundOptions.map(sound => (
                <label 
                  key={sound.id}
                  className={`cursor-pointer p-3 rounded-lg border flex items-center ${
                    formData.sound === sound.id 
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' 
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="sound"
                    value={sound.id}
                    checked={formData.sound === sound.id}
                    onChange={handleInputChange}
                    className="form-radio mr-2"
                  />
                  <div className="flex items-center">
                    {sound.id !== 'none' && <Bell className="w-4 h-4 mr-2 text-indigo-500" />}
                    <span className="text-sm text-gray-700 dark:text-gray-300">{sound.name}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Тип уведомления */}
          <div className="mb-6">
            <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Способ уведомления
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="inline-flex items-center p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600">
                <input
                  type="radio"
                  name="notificationType"
                  value="browser"
                  checked={formData.notificationType === 'browser'}
                  onChange={handleInputChange}
                  className="form-radio"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Браузерное уведомление
                </span>
              </label>
              <label className="inline-flex items-center p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600">
                <input
                  type="radio"
                  name="notificationType"
                  value="none"
                  checked={formData.notificationType === 'none'}
                  onChange={handleInputChange}
                  className="form-radio"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Без уведомлений
                </span>
              </label>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-1" />
              <div>
                <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                  О браузерных уведомлениях
                </h4>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Браузерные уведомления будут отображаться даже если вкладка с таймером неактивна.
                  При первом использовании браузер запросит разрешение на показ уведомлений.
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Кнопки действий */}
      <div className="mt-8 flex flex-wrap gap-4">
        <Button
          type="submit"
          primary
          icon={<PlayCircle className="w-5 h-5" />}
          disabled={isRunning}
          size="lg"
        >
          Запустить таймер
        </Button>
        <Button
          type="button"
          onClick={handleSaveTimer}
          icon={<Save className="w-5 h-5" />}
          variant="secondary"
          size="lg"
        >
          Сохранить настройки
        </Button>
      </div>
    </form>
  );
};

export default TimerForm;